/**
 * events/EventContractRegistry.ts — EVENT CONTRACT SYSTEM™ (build 1.89.0).
 *
 * Every event on the Core Event Bus is a first-class platform contract:
 * name, version, description, publishers, intended subscribers, payload
 * schema (required/optional fields), timestamp policy, stability status and
 * a deprecation flag. The registry validates payloads at publish time.
 *
 * Design constraints (sprint contract):
 * - O(1) contract lookup (Map keyed by event name).
 * - Validation runs ONLY on publish, is O(declared fields), and allocates
 *   nothing on the happy path (problems array is created lazily on failure).
 * - Validation NEVER throws and NEVER blocks a publish: development warns
 *   loudly on the console; production records the warning silently. User
 *   workflows are never interrupted.
 * - Fully additive: the registry starts empty ("unarmed") — a bus with no
 *   registered contracts behaves exactly as before this sprint, so existing
 *   unit tests and any bare CoreEventBus keep working unchanged.
 */

import type { CoreEventName } from './CoreEventBus';

export type EventFieldType = 'string' | 'number' | 'boolean' | 'object' | 'array';

export interface EventFieldSpec {
  name: string;
  type: EventFieldType;
  required: boolean;
  description: string;
}

export type EventStability = 'stable' | 'observability' | 'reserved';

export interface EventContract {
  /** Event name — the contract key (one contract per event). */
  name: CoreEventName;
  /** Payload schema version. Current generation: 1. */
  version: number;
  description: string;
  /** Known production publishers (source strings as they appear on the bus). */
  publishers: string[];
  /** Intended subscribers (subscriber ids as registered on the bus). */
  subscribers: string[];
  /** Declared payload fields. Extra fields are allowed only when allowExtraFields. */
  fields: EventFieldSpec[];
  /**
   * Some payloads are intentionally open (e.g. NotificationSent spreads
   * module-specific detail). When false, undeclared fields raise a warning.
   */
  allowExtraFields: boolean;
  /** How event.at is set, in plain English. */
  timestampPolicy: string;
  stability: EventStability;
  /** Future use — no contract is deprecated in generation 1. */
  deprecated: boolean;
  /** Example payload for the Developer Center details panel. */
  example: Record<string, unknown> | null;
}

export interface ValidationProblem {
  event: string;
  publisher: string;
  at: number;
  problems: string[];
}

export interface ContractValidationResult {
  ok: boolean;
  /** null when ok — no allocation on the happy path. */
  problems: string[] | null;
}

export interface ContractRegistrySnapshot {
  totalContracts: number;
  versions: Partial<Record<CoreEventName, number>>;
  deprecatedCount: number;
  validated: number;
  passed: number;
  failed: number;
  /** Publishes of events with no registered contract (post-arming). */
  unknownEvents: number;
  lastFailure: ValidationProblem | null;
}

const WARNING_LOG_LIMIT = 50;
const SUPPORTED_VERSION = 1;

function isDevMode(): boolean {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

function typeOf(value: unknown): EventFieldType | 'null' | 'other' {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return 'array';
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') return t;
  if (t === 'object') return 'object';
  return 'other';
}

export class EventContractRegistry {
  private contracts = new Map<CoreEventName, EventContract>();
  /** Duplicate registration attempts (contract integrity signal). */
  private duplicateRegistrations: string[] = [];
  private warnings: ValidationProblem[] = [];
  private counters = { validated: 0, passed: 0, failed: 0, unknownEvents: 0 };
  private lastFailure: ValidationProblem | null = null;

  /** Register a contract. Duplicate names are recorded, first one wins. */
  register(contract: EventContract): void {
    if (this.contracts.has(contract.name)) {
      this.duplicateRegistrations.push(contract.name);
      this.warn({ event: contract.name, publisher: 'registry', at: Date.now(), problems: [`Duplicate contract registration for "${contract.name}" — first registration wins`] });
      return;
    }
    if (contract.version !== SUPPORTED_VERSION) {
      this.warn({ event: contract.name, publisher: 'registry', at: Date.now(), problems: [`Contract version ${contract.version} is not the supported generation (${SUPPORTED_VERSION})`] });
    }
    this.contracts.set(contract.name, contract);
  }

  /** O(1) lookup. */
  get(name: CoreEventName): EventContract | undefined {
    return this.contracts.get(name);
  }

  list(): EventContract[] {
    return [...this.contracts.values()];
  }

  /** True once any contract is registered — validation is active. */
  get armed(): boolean {
    return this.contracts.size > 0;
  }

  getDuplicateRegistrations(): string[] {
    return [...this.duplicateRegistrations];
  }

  /**
   * Validate a payload against its contract. Pure check — no logging, no
   * counters. Used by tests and the Dev Center; publish path uses
   * validateForPublish which also records outcomes.
   */
  validate(name: CoreEventName, payload: unknown): ContractValidationResult {
    const contract = this.contracts.get(name);
    if (!contract) return { ok: false, problems: [`No contract registered for event "${name}"`] };

    let problems: string[] | null = null;
    const add = (p: string) => { (problems ??= []).push(p); };

    if (payload === null || payload === undefined) {
      if (contract.fields.some((f) => f.required)) add('Payload is null/undefined but contract declares required fields');
    } else if (typeOf(payload) !== 'object') {
      add(`Payload must be an object, got ${typeOf(payload)}`);
    } else {
      const record = payload as Record<string, unknown>;
      for (const field of contract.fields) {
        const value = record[field.name];
        if (value === undefined || value === null) {
          if (field.required) add(`Missing required field "${field.name}" (${field.type})`);
          continue;
        }
        const actual = typeOf(value);
        if (actual !== field.type) add(`Field "${field.name}" expected ${field.type}, got ${actual}`);
      }
      if (!contract.allowExtraFields) {
        for (const key of Object.keys(record)) {
          if (!contract.fields.some((f) => f.name === key)) add(`Unexpected field "${key}" (contract does not allow extra fields)`);
        }
      }
    }
    return problems === null ? { ok: true, problems: null } : { ok: false, problems };
  }

  /**
   * Publish-path validation. Never throws, never blocks. Dev mode warns
   * loudly on the console; production records silently.
   */
  validateForPublish(name: CoreEventName, publisher: string, payload: unknown, at: number): void {
    if (!this.armed) return; // pre-contract world: behave exactly as before

    const contract = this.contracts.get(name);
    if (!contract) {
      this.counters.unknownEvents += 1;
      this.warn({ event: name, publisher, at, problems: [`Unknown event "${name}" — no contract registered`] });
      return;
    }

    this.counters.validated += 1;
    const result = this.validate(name, payload);
    if (result.ok) {
      this.counters.passed += 1;
      return;
    }
    this.counters.failed += 1;
    const failure: ValidationProblem = { event: name, publisher, at, problems: result.problems ?? [] };
    this.lastFailure = failure;
    this.warn(failure);
  }

  private warn(problem: ValidationProblem): void {
    this.warnings.push(problem);
    if (this.warnings.length > WARNING_LOG_LIMIT) this.warnings.shift();
    if (isDevMode()) {
      // Development: warn loudly. Production: silent record only.
      // eslint-disable-next-line no-console
      console.warn(`[event-contract] ${problem.event} (publisher: ${problem.publisher}) — ${problem.problems.join('; ')}`);
    }
  }

  getWarnings(): ValidationProblem[] {
    return [...this.warnings];
  }

  getSnapshot(): ContractRegistrySnapshot {
    const versions: Partial<Record<CoreEventName, number>> = {};
    let deprecatedCount = 0;
    for (const c of this.contracts.values()) {
      versions[c.name] = c.version;
      if (c.deprecated) deprecatedCount += 1;
    }
    return {
      totalContracts: this.contracts.size,
      versions,
      deprecatedCount,
      ...this.counters,
      lastFailure: this.lastFailure ? { ...this.lastFailure, problems: [...this.lastFailure.problems] } : null,
    };
  }

  /**
   * Startup / integrity-audit verification. Checks every known event name has
   * a contract, versions are the supported generation, and no duplicates were
   * attempted. Returns human-readable findings (empty = fully consistent).
   */
  verifyCoverage(allEventNames: readonly CoreEventName[]): string[] {
    const findings: string[] = [];
    for (const name of allEventNames) {
      if (!this.contracts.has(name)) findings.push(`Missing contract: "${name}" has no registered contract`);
    }
    for (const c of this.contracts.values()) {
      if (!allEventNames.includes(c.name)) findings.push(`Orphan contract: "${c.name}" is not a known CoreEventName`);
      if (c.version !== SUPPORTED_VERSION) findings.push(`Version mismatch: "${c.name}" is v${c.version}, supported generation is v${SUPPORTED_VERSION}`);
    }
    for (const dup of this.duplicateRegistrations) findings.push(`Duplicate contract registration: "${dup}"`);
    return findings;
  }
}

/** The platform-wide registry singleton (armed by registerAllEventContracts). */
export const eventContractRegistry = new EventContractRegistry();
